import { CarFront, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { GroupCard } from "@/components/groups/group-card";
import { JourneyBar } from "@/components/groups/journey-bar";
import { MyGroupCard } from "@/components/groups/my-group-card";
import { PendingRequestCard } from "@/components/groups/pending-request-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useGroups, useMyGroups } from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import { formatToday, todayWeekday } from "@/lib/format";
import { myDays } from "@/lib/groups";
import type { GroupDetail } from "@/types/api";

/**
 * The adaptive home screen — settled members see THEIR crews first; the
 * browse list serves seekers and members hunting a crew for their other days.
 */
export function HomePage() {
  const { user } = useAuth();
  const { data: myGroups, isLoading: mineLoading, isError: mineError } = useMyGroups();
  const { data: groups, isLoading: groupsLoading, isError: groupsError } = useGroups();
  const [plantFilter, setPlantFilter] = useState<string | null>(null);

  const mine = myGroups ?? [];
  const daysOf = (group: GroupDetail) => myDays(group, user?.id);
  const today = todayWeekday();
  // Today's ride on top: with several crews, the card that matters this
  // morning must not sit below one that stays home today.
  const settled = mine
    .filter((group) => group.my_membership_status !== "requested")
    .sort((a, b) => Number(daysOf(b).includes(today)) - Number(daysOf(a).includes(today)));
  const pending = mine.filter((group) => group.my_membership_status === "requested");
  // Anyone may open a group for the weekdays they are not already spoken for,
  // as a passenger or behind their own wheel: shifts differ day to day, so a
  // passenger on Monday is often a driver on Tuesday (client, 2026-10-14), and
  // a driver may run a 06:00 crew Mon–Wed and a 14:00 one Thu–Fri (2026-10-21).
  const hasFreeDay = new Set(mine.flatMap(daysOf)).size < 7;

  let title: string = t.appName;
  if (settled.length === 1) title = t.home.yourCrew;
  if (settled.length > 1) title = t.home.yourCrews;

  const mineIds = new Set(mine.map((group) => group.id));
  const browseList = (groups ?? []).filter(
    (group) =>
      !mineIds.has(group.id) && (plantFilter === null || group.plant.id === plantFilter),
  );

  return (
    <div className="flex flex-col gap-6 sm:gap-8">
      <header className="animate-in fade-in duration-500">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground text-sm first-letter:uppercase">{formatToday()}</p>
      </header>

      {mineLoading && <Skeleton className="h-72 w-full rounded-2xl" />}

      {settled.map((group) => (
        <MyGroupCard key={group.id} group={group} />
      ))}

      {pending.map((group) => (
        <PendingRequestCard
          key={group.id}
          group={group}
          action={
            <Button asChild variant="outline" size="sm">
              <Link to={`/grupy/${group.id}`}>{t.common.details}</Link>
            </Button>
          }
        />
      ))}

      {/* A failed fetch is not "you have no crew": offline, or with a revoked
          session, a settled passenger must not be told their ride is gone. */}
      {mineError && (
        <p className="text-destructive rounded-xl border border-dashed p-4 text-center text-sm">
          {t.common.loadError}
        </p>
      )}

      {!mineLoading && !mineError && mine.length === 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-2 bg-card flex flex-col items-center gap-3 rounded-2xl border border-dashed p-6 text-center duration-500 sm:p-8">
          <CarFront className="text-muted-foreground size-10" aria-hidden />
          <h2 className="text-lg font-semibold">{t.home.noGroupYet}</h2>
          <p className="text-muted-foreground max-w-md text-sm">{t.home.noGroupYetBody}</p>
          <Button asChild variant="outline" className="mt-1">
            <Link to="/grupy/nowa">
              <Plus aria-hidden /> {t.home.createGroup}
            </Link>
          </Button>
        </div>
      )}

      {/* Everyone browses — a member with Mon–Tue covered may still need
          Wed–Fri, and a driver may ride along on the days their car stays
          home. The join dialog hides days already spoken for. */}
      <section aria-label={t.home.browseTitle} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">{t.home.browseTitle}</h2>
            <p className="text-muted-foreground text-sm">{t.home.browseSubtitle}</p>
          </div>
          {/* The empty state above already carries this call to action. */}
          {mine.length > 0 && hasFreeDay && (
            <Button asChild variant="outline" size="sm">
              <Link to="/grupy/nowa">
                <Plus aria-hidden /> {t.home.createGroup}
              </Link>
            </Button>
          )}
        </div>

        <JourneyBar plantFilter={plantFilter} onPlantFilterChange={setPlantFilter} />

        {groupsLoading && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        )}

        {browseList.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {browseList.map((group, index) => (
              <GroupCard key={group.id} group={group} index={index} />
            ))}
          </div>
        )}

        {!groupsLoading && browseList.length === 0 && (
          <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
            {/* The list leaves out my own crews, so "no groups yet" would be
                wrong for anyone who already drives or rides with one — and
                under a plant filter it only speaks for that plant. */}
            {groupsError
              ? t.common.loadError
              : plantFilter !== null
                ? t.home.noGroupsForPlant
                : mine.length > 0
                  ? t.home.noOtherGroups
                  : t.home.noGroups}
          </p>
        )}
      </section>
    </div>
  );
}
